import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  LayoutDashboard, Ticket, LogOut, Search, Trash2, X,
  CheckCircle, Clock, FileText, Package, Plus, Shield, Users, ExternalLink,
  ShieldCheck, UserCircle, Briefcase, MessageSquare
} from "lucide-react";
import TicketChat from "../components/TicketChat";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
import Logo from "../components/Logo";

const STATUSES = [
  { key: "all", label: "All", color: "gray" },
  { key: "inquiry_submitted", label: "Inquiry Submitted", color: "blue" },
  { key: "under_review", label: "Under Review", color: "amber" },
  { key: "plan_confirmed", label: "Plan Confirmed", color: "purple" },
  { key: "fully_booked", label: "Fully Booked", color: "green" },
];

const STATUS_COLORS = {
  inquiry_submitted: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  under_review: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  plan_confirmed: { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  fully_booked: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
};

function StatusBadge({ status }) {
  const col = STATUS_COLORS[status] || { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-400" };
  const label = STATUSES.find(s => s.key === status)?.label || status;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${col.bg} ${col.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
      {label}
    </span>
  );
}

function TicketDetailPanel({ ticket, onClose, onUpdate }) {
  const { user } = useAuth();
  const [panelTab, setPanelTab] = useState("details"); // "details" | "chat"
  const [form, setForm] = useState({
    status: ticket.status,
    treatment_details: ticket.treatment_details || "",
    travel_info: ticket.travel_info || "",
    accommodation: ticket.accommodation || "",
    pricing: ticket.pricing || "",
    timeline: ticket.timeline || "",
    provider: ticket.provider || "",
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(ticket.assigned_to || "");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/workers`, { withCredentials: true })
      .then(r => setWorkers(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== "") payload[k] = v; });
      const res = await axios.patch(`${API}/admin/tickets/${ticket.ticket_id}`, payload, { withCredentials: true });
      onUpdate(res.data);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleAssign = async () => {
    setAssigning(true);
    try {
      const res = await axios.patch(`${API}/admin/tickets/${ticket.ticket_id}/assign`,
        { worker_id: selectedWorker }, { withCredentials: true });
      onUpdate(res.data);
    } catch (e) { console.error(e); }
    setAssigning(false);
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticket.ticket_id}/notes`, { text: note }, { withCredentials: true });
      setNote("");
      const res = await axios.get(`${API}/admin/tickets/${ticket.ticket_id}`, { withCredentials: true });
      onUpdate(res.data);
    } catch (e) { console.error(e); }
    setAddingNote(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ticket ${ticket.ticket_id}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/tickets/${ticket.ticket_id}`, { withCredentials: true });
      onClose(true);
    } catch (e) { console.error(e); setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ticket-detail-panel">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => onClose()} />
      <div className="relative ml-auto w-full max-w-[700px] bg-white/95 backdrop-blur-3xl shadow-2xl h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl font-black text-[#0B3C5D] font-outfit" data-testid="panel-ticket-id">{ticket.ticket_id}</span>
              <StatusBadge status={form.status} />
              {ticket.assigned_to_name && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                  <Briefcase className="w-3 h-3" /> {ticket.assigned_to_name}
                </span>
              )}
            </div>
            <p className="text-sm text-[#6B7280]">{ticket.name} · {ticket.email}</p>
          </div>
          <button onClick={() => onClose()} className="p-2 rounded-xl hover:bg-gray-100 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {/* Panel Tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0">
          {[["details", "Details"], ["chat", "Messages"]].map(([k, l]) => (
            <button key={k} onClick={() => setPanelTab(k)}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${panelTab === k ? "text-[#0B3C5D] border-b-2 border-[#0B3C5D] bg-white" : "text-[#6B7280] bg-[#F8FAFC] hover:text-[#0B3C5D]"}`}>
              {k === "chat" && <MessageSquare className="w-3.5 h-3.5" />} {l}
            </button>
          ))}
        </div>

        {panelTab === "chat" ? (
          <div className="flex-1 p-4 overflow-hidden flex flex-col">
            <TicketChat ticketId={ticket.ticket_id} />
          </div>
        ) : (
        <div className="flex-1 p-6 space-y-6 overflow-y-auto">
          {/* Patient Info */}
          <section>
            <h3 className="font-bold text-[#0B3C5D] text-sm uppercase tracking-wide mb-3">Patient Information</h3>
            <div className="grid grid-cols-2 gap-3">
              {[["Name", ticket.name], ["Email", ticket.email], ["Phone", ticket.phone], ["Treatment", ticket.treatment], ["Language", ticket.language?.toUpperCase()], ["Submitted", new Date(ticket.created_at).toLocaleDateString()]].map(([l, v]) => (
                <div key={l} className="bg-[#F8FAFC] rounded-xl p-3">
                  <p className="text-xs text-[#6B7280] mb-0.5">{l}</p>
                  <p className="text-sm font-semibold text-[#0B3C5D]">{v}</p>
                </div>
              ))}
              {ticket.message && (
                <div className="col-span-2 bg-[#F8FAFC] rounded-xl p-3">
                  <p className="text-xs text-[#6B7280] mb-0.5">Message</p>
                  <p className="text-sm text-gray-700">{ticket.message}</p>
                </div>
              )}
            </div>
          </section>

          {/* Assign Worker */}
          <section>
            <h3 className="font-bold text-[#0B3C5D] text-sm uppercase tracking-wide mb-3">Assign Worker</h3>
            <div className="flex gap-2">
              <select value={selectedWorker} onChange={e => setSelectedWorker(e.target.value)}
                data-testid="assign-worker-select"
                className="form-input text-sm flex-1">
                <option value="">— Unassigned —</option>
                {workers.map(w => (
                  <option key={w.user_id} value={w.user_id}>
                    {w.name} ({w.role === "admin" ? "Admin" : "Worker"})
                  </option>
                ))}
              </select>
              <button onClick={handleAssign} disabled={assigning}
                data-testid="assign-worker-button"
                className="px-4 py-2.5 bg-[#0B3C5D] text-white text-sm font-semibold rounded-xl hover:bg-[#3FA7D6] transition-colors disabled:opacity-50 whitespace-nowrap">
                {assigning ? "..." : "Assign"}
              </button>
            </div>
            {ticket.assigned_to_name && (
              <p className="text-xs text-[#6B7280] mt-2 flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> Currently assigned to <span className="font-semibold text-[#0B3C5D]">{ticket.assigned_to_name}</span>
              </p>
            )}
          </section>

          {/* Status */}
          <section>
            <h3 className="font-bold text-[#0B3C5D] text-sm uppercase tracking-wide mb-3">Update Status</h3>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.filter(s => s.key !== "all").map(s => (
                <button key={s.key} onClick={() => setForm(f => ({ ...f, status: s.key }))}
                  data-testid={`status-option-${s.key}`}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${form.status === s.key ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" : "border-gray-200 text-gray-600 hover:border-[#3FA7D6]"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </section>

          {/* Plan Details */}
          <section>
            <h3 className="font-bold text-[#0B3C5D] text-sm uppercase tracking-wide mb-3">Plan Details</h3>
            <div className="space-y-3">
              {[
                ["provider", "Medical Provider / Hospital"],
                ["pricing", "Pricing Breakdown"],
                ["treatment_details", "Treatment Details"],
                ["travel_info", "Travel Arrangements"],
                ["accommodation", "Accommodation"],
                ["timeline", "Timeline & Schedule"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-[#6B7280] mb-1">{label}</label>
                  <textarea
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    rows={key === "treatment_details" || key === "timeline" ? 3 : 2}
                    placeholder={`Enter ${label.toLowerCase()}...`}
                    data-testid={`plan-field-${key}`}
                    className="form-input resize-none text-sm"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Internal Notes */}
          <section>
            <h3 className="font-bold text-[#0B3C5D] text-sm uppercase tracking-wide mb-3">Internal Notes</h3>
            {(ticket.internal_notes || []).length > 0 ? (
              <div className="space-y-2 mb-3">
                {ticket.internal_notes.map((n, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-sm text-gray-700">{n.text}</p>
                    <p className="text-xs text-[#6B7280] mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#6B7280] mb-3">No internal notes yet.</p>}
            <div className="flex gap-2">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..."
                data-testid="note-input"
                onKeyDown={e => e.key === "Enter" && handleAddNote()}
                className="form-input text-sm flex-1" />
              <button onClick={handleAddNote} disabled={addingNote || !note.trim()}
                data-testid="add-note-button"
                className="px-4 py-2.5 bg-[#0B3C5D] text-white text-sm font-semibold rounded-xl hover:bg-[#3FA7D6] transition-colors disabled:opacity-50 flex items-center gap-1">
                <Plus className="w-4 h-4" /> {addingNote ? "..." : "Add"}
              </button>
            </div>
          </section>
        </div>
        )} {/* end details tab */}

        {/* Footer - only show in details tab */}
        {panelTab === "details" && (
        <div className="p-6 border-t border-gray-100 flex gap-3 flex-shrink-0 bg-white">
          <button onClick={handleSave} disabled={saving}
            data-testid="save-ticket-button"
            className="flex-1 py-3 bg-[#0B3C5D] text-white font-semibold rounded-xl hover:bg-[#3FA7D6] transition-colors disabled:opacity-50">
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <button onClick={handleDelete} disabled={deleting}
            data-testid="delete-ticket-button"
            className="px-4 py-3 bg-red-50 text-red-600 font-semibold rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("tickets"); // "tickets" | "users"
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statView, setStatView] = useState("total");

  const fetchTickets = useCallback(async (fStatus = statusFilter, fSearch = search) => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/tickets`, {
          params: { status: fStatus !== "all" ? fStatus : undefined, search: fSearch || undefined },
          withCredentials: true
        }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setTickets(tRes.data);
      setStats(sRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter, search]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (e) { console.error(e); }
    setUsersLoading(false);
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewChange = (v) => {
    setView(v);
    if (v === "users") fetchUsers();
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    fetchTickets(statusFilter, val);
  };

  const handleStatusFilter = (s) => {
    setStatusFilter(s);
    fetchTickets(s, search);
  };

  const handleTicketUpdate = (updated) => {
    setTickets(prev => prev.map(t => t.ticket_id === updated.ticket_id ? updated : t));
    setSelectedTicket(updated);
    fetchTickets(statusFilter, search);
  };

  const handlePanelClose = (deleted = false) => {
    setSelectedTicket(null);
    if (deleted) fetchTickets(statusFilter, search);
  };

  const handleRoleChange = async (targetUser, newRole) => {
    if (targetUser.user_id === user?.user_id && newRole !== "admin") {
      if (!window.confirm("You are removing your own admin role. Are you sure?")) return;
    }
    try {
      await axios.patch(`${API}/admin/users/${targetUser.user_id}/role`, { role: newRole }, { withCredentials: true });
      setUsers(prev => prev.map(u => u.user_id === targetUser.user_id ? { ...u, role: newRole } : u));
    } catch (e) { console.error(e); }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const statValue = stats ? (statView === "total" ? stats.total : stats.new_this_week) : 0;
  const statLabel = statView === "total" ? "Total Queries" : "New This Week";

  const navItems = [
    { key: "tickets", icon: LayoutDashboard, label: "Dashboard", badge: null },
    { key: "tickets", icon: Ticket, label: "All Tickets", badge: stats?.total },
    { key: "users", icon: Users, label: "Users", badge: users.length || null },
  ];

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0B3C5D] flex-shrink-0 hidden lg:flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="p-6 border-b border-white/10">
          <Link to="/"><Logo white /></Link>
          <span className="text-xs font-bold uppercase tracking-widest text-[#3FA7D6] mt-3 block">Admin Panel</span>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => handleViewChange("tickets")} data-testid="nav-dashboard"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${view === "tickets" ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"}`}>
            <LayoutDashboard className="w-4 h-4" /> Dashboard
            {stats?.total !== undefined && <span className="ml-auto text-xs bg-[#3FA7D6] text-white px-2 py-0.5 rounded-full">{stats.total}</span>}
          </button>
          <button onClick={() => handleViewChange("users")} data-testid="nav-users"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${view === "users" ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"}`}>
            <Users className="w-4 h-4" /> Users
            {users.length > 0 && <span className="ml-auto text-xs bg-white/20 text-white/80 px-2 py-0.5 rounded-full">{users.length}</span>}
          </button>
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture
              ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-[#3FA7D6]" />
              : <div className="w-8 h-8 rounded-full bg-[#3FA7D6]/30 flex items-center justify-center"><UserCircle className="w-5 h-5 text-[#3FA7D6]" /></div>
            }
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="flex-1 flex items-center justify-center gap-1 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white/70 text-xs transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Website
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout-button"
              className="flex-1 flex items-center justify-center gap-1 py-2 bg-white/10 hover:bg-red-500/30 rounded-xl text-white/70 hover:text-white text-xs transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        {/* Mobile header */}
        <div className="lg:hidden bg-[#0B3C5D] px-6 py-4 flex items-center justify-between">
          <Logo white />
          <div className="flex items-center gap-3">
            <button onClick={() => handleViewChange(view === "tickets" ? "users" : "tickets")}
              className="text-white/70 text-sm flex items-center gap-1">
              {view === "tickets" ? <Users className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}
              {view === "tickets" ? "Users" : "Tickets"}
            </button>
            <button onClick={handleLogout} className="text-white/70 text-sm flex items-center gap-1">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="p-6 lg:p-8">
          {view === "tickets" ? (
            <>
              {/* ── TICKETS VIEW ── */}
              <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#0B3C5D] font-outfit" data-testid="admin-dashboard-title">Admin Dashboard</h1>
                <p className="text-[#6B7280] mt-1 text-sm">Manage all consultation queries and track patient journeys.</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="col-span-2 glass-card rounded-3xl p-6" data-testid="main-stat-card">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">{statLabel}</p>
                      <p className="text-5xl font-black text-[#0B3C5D] font-outfit">{statValue}</p>
                    </div>
                    <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                      <button onClick={() => setStatView("total")} data-testid="stat-view-total"
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${statView === "total" ? "bg-white text-[#0B3C5D] shadow-sm" : "text-[#6B7280] hover:text-[#0B3C5D]"}`}>Total</button>
                      <button onClick={() => setStatView("week")} data-testid="stat-view-week"
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${statView === "week" ? "bg-white text-[#0B3C5D] shadow-sm" : "text-[#6B7280] hover:text-[#0B3C5D]"}`}>This Week</button>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100">
                    {stats && Object.entries(stats.by_status).map(([key, val]) => {
                      const col = STATUS_COLORS[key];
                      return (
                        <div key={key} className="text-center">
                          <div className={`text-lg font-black font-outfit ${col?.text || "text-gray-700"}`}>{val}</div>
                          <div className="text-xs text-[#6B7280] mt-0.5 capitalize">{key.replace(/_/g, " ")}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {stats && STATUSES.filter(s => s.key !== "all").slice(0, 2).map(s => {
                  const col = STATUS_COLORS[s.key];
                  return (
                    <div key={s.key} className="glass-card rounded-3xl p-6 hover-lift" data-testid={`stat-${s.key}`}>
                      <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">{s.label}</p>
                      <p className={`text-4xl font-black font-outfit ${col?.text}`}>{stats.by_status[s.key] || 0}</p>
                    </div>
                  );
                })}
              </div>

              {/* Tickets Table */}
              <div className="glass-card rounded-3xl overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                    <input value={search} onChange={handleSearch} placeholder="Search by name, email, ID, treatment..."
                      data-testid="admin-search"
                      className="w-full pl-10 pr-4 py-2.5 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3FA7D6] focus:ring-2 focus:ring-[#3FA7D6]/10" />
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {STATUSES.map(s => (
                      <button key={s.key} onClick={() => handleStatusFilter(s.key)} data-testid={`filter-${s.key}`}
                        className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${statusFilter === s.key ? "bg-[#0B3C5D] text-white" : "bg-[#F8FAFC] text-[#6B7280] hover:bg-gray-100"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-4 border-[#3FA7D6] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-16 text-[#6B7280]" data-testid="no-results">
                    <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="font-medium">No tickets found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="tickets-table">
                      <thead>
                        <tr className="text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wide border-b border-gray-100">
                          {["Ticket ID", "Patient", "Treatment", "Status", "Date", ""].map(h => (
                            <th key={h} className="px-5 py-3.5">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map(ticket => (
                          <tr key={ticket.ticket_id}
                            className="border-b border-gray-50 hover:bg-[#F8FAFC] transition-colors cursor-pointer group"
                            onClick={() => setSelectedTicket(ticket)}
                            data-testid={`ticket-row-${ticket.ticket_id}`}>
                            <td className="px-5 py-4"><span className="font-black text-[#0B3C5D] font-outfit">{ticket.ticket_id}</span></td>
                            <td className="px-5 py-4">
                              <div className="font-semibold text-[#0B3C5D] text-sm">{ticket.name}</div>
                              <div className="text-xs text-[#6B7280]">{ticket.email}</div>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-700">{ticket.treatment}</td>
                            <td className="px-5 py-4"><StatusBadge status={ticket.status} /></td>
                            <td className="px-5 py-4 text-xs text-[#6B7280]">{new Date(ticket.created_at).toLocaleDateString()}</td>
                            <td className="px-5 py-4">
                              <span className="text-xs text-[#3FA7D6] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">Open →</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="px-5 py-3 text-xs text-[#6B7280] border-t border-gray-100">
                      {tickets.length} ticket{tickets.length !== 1 ? "s" : ""} shown
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ── USERS VIEW ── */}
              <div className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#0B3C5D] font-outfit" data-testid="users-view-title">User Management</h1>
                <p className="text-[#6B7280] mt-1 text-sm">Manage user accounts and assign admin roles.</p>
              </div>

              <div className="glass-card rounded-3xl overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#3FA7D6]" />
                    <span className="font-semibold text-[#0B3C5D] text-sm">{users.length} registered user{users.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#3FA7D6]/10 text-[#3FA7D6] rounded-full text-xs font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5" /> {users.filter(u => u.role === "admin").length} admin{users.filter(u => u.role === "admin").length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {usersLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-4 border-[#3FA7D6] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-16 text-[#6B7280]">
                    <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="font-medium">No users found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="users-table">
                      <thead>
                        <tr className="text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wide border-b border-gray-100">
                          {["User", "Email", "Role", "Joined", "Actions"].map(h => (
                            <th key={h} className="px-5 py-3.5">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.user_id} className="border-b border-gray-50 hover:bg-[#F8FAFC] transition-colors" data-testid={`user-row-${u.user_id}`}>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                {u.picture
                                  ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full border border-gray-200" />
                                  : <div className="w-9 h-9 rounded-full bg-[#3FA7D6]/15 flex items-center justify-center">
                                      <UserCircle className="w-5 h-5 text-[#3FA7D6]" />
                                    </div>
                                }
                                <div>
                                  <div className="font-semibold text-[#0B3C5D] text-sm">{u.name}</div>
                                  {u.user_id === user?.user_id && (
                                    <div className="text-xs text-[#3FA7D6] font-medium">You</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 text-sm text-[#6B7280]">{u.email}</td>
                            <td className="px-5 py-4">
                              {u.role === "admin" ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#0B3C5D] text-white rounded-full text-xs font-semibold">
                                  <ShieldCheck className="w-3.5 h-3.5" /> Admin
                                </span>
                              ) : u.role === "worker" ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                  <Briefcase className="w-3.5 h-3.5" /> Worker
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                                  <UserCircle className="w-3.5 h-3.5" /> User
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-xs text-[#6B7280]">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex gap-1">
                                {[["user", "User", "bg-gray-100 text-gray-600"],
                                  ["worker", "Worker", "bg-purple-100 text-purple-700"],
                                  ["admin", "Admin", "bg-[#0B3C5D] text-white"]].map(([r, label, activeClass]) => (
                                  <button key={r}
                                    onClick={() => u.role !== r && handleRoleChange(u, r)}
                                    data-testid={`set-role-${r}-${u.user_id}`}
                                    disabled={u.role === r}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${u.role === r ? activeClass + " cursor-default" : "bg-gray-100 text-gray-400 hover:bg-gray-200 cursor-pointer"}`}>
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="px-5 py-3 text-xs text-[#6B7280] border-t border-gray-100">
                      {users.length} user{users.length !== 1 ? "s" : ""} total · {users.filter(u => u.role === "admin").length} admin{users.filter(u => u.role === "admin").length !== 1 ? "s" : ""}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Ticket Detail Panel */}
      {selectedTicket && (
        <TicketDetailPanel ticket={selectedTicket} onClose={handlePanelClose} onUpdate={handleTicketUpdate} />
      )}
    </div>
  );
}
